/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { Fragment, useEffect, useState } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import { useFlags } from '@atlaskit/flag';
import QuestionCircleIcon from '@atlaskit/icon/core/question-circle';
import ErrorIcon from '@atlaskit/icon/core/status-error';
import CheckCircleIcon from '@atlaskit/icon/core/status-success';
import Link from '@atlaskit/link';
import Modal, {
	ModalFooter,
	ModalHeader,
	ModalTitle,
	ModalTransition,
} from '@atlaskit/modal-dialog';
import { Box, Flex, Inline, Text } from '@atlaskit/primitives/compiled';
import Select, { CreatableSelect, type InputActionMeta } from '@atlaskit/select';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import { useAnalytics } from '../../../../common/controller/analytics/use-analytics';
import { logger } from '../../../../common/utils/logger';
import { useAddBookmarkMutation } from '../bookmark-api/useAddBookmarkMutation';
import type { Bookmark } from '../bookmark-api/useBookmarkQuery/fetch-bookmark';
import { useUpdateBookmarkMutation } from '../bookmark-api/useUpdateBookmarkMutation';
import { convertToKeyPhraseInputFormat } from '../convert-to-key-phrase-input-format';
import { getAdminhubLink } from '../get-adminhub-link';
import { messages } from '../messages';
import { onAddPin } from '../on-add-pin';
import { onAddPinModalOpen } from '../on-add-pin-modal-open';
import { onBookmarkAddFailed } from '../on-bookmark-add-failed';
import { onUpdatePin } from '../on-update-pin';

import { isValidHttpUrl } from './is-valid-http-url';
import { getCollidedKeyPhrases, getKeyPhraseErrMessage, SearchPinKeyphraseErrorState } from './validation';

const styles = cssMap({
	modalBody: {
		paddingLeft: token('space.300'),
		paddingRight: token('space.300'),
	},
	formFieldsContainer: {
		marginTop: token('space.300'),
	},
});

type PinUrlErrorType =
	| {
			isError: false;
			type?: never;
	  }
	| {
			isError: true;
			type: 'empty' | 'invalid';
	  };

export interface PinSearchTermOption {
	label: string;
	value: string;
}

interface SearchPinModalProps {
	handleClose(): void;
	orgId: string;
	cloudId: string;
	onSuccessCallback?(): void;
	hasAdminhubAccess: boolean;
	query?: string;
	workspaceId: string;
	product: string;
	existingBookmark?: Bookmark;
	source?: 'adminOnboarding' | 'popularSearchQuery';
}

const KEY_PHRASE_MAX_LENGTH = 50;
const KEY_PHRASES_LIST_MAX_LENGTH = 5;

export const AddPinModal: ({
	orgId,
	cloudId,
	hasAdminhubAccess,
	handleClose,
	onSuccessCallback,
	query,
	workspaceId,
	product,
	existingBookmark,
	source,
}: SearchPinModalProps) => JSX.Element = ({
	orgId,
	cloudId,
	hasAdminhubAccess,
	handleClose,
	onSuccessCallback,
	query,
	workspaceId,
	product,
	existingBookmark,
	source,
}: SearchPinModalProps) => {
	const { formatList, formatMessage } = useIntl();
	const [fireAnalyticsEvent] = useAnalytics();
	const { showFlag } = useFlags();

	const isUpdateModal = !!existingBookmark;

	useEffect(() => {
		fireAnalyticsEvent(onAddPinModalOpen(isUpdateModal, source));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const [hasGenericError, setHasGenericError] = React.useState<boolean>(false);

	const [pinTitle, setPinTitle] = React.useState(
		isUpdateModal ? existingBookmark.properties.title : query || '',
	);
	const [pinTitleErr, setPinTitleErr] = React.useState(false);
	const onPinTitleChange = (input: string, actionMeta: InputActionMeta) => {
		if (actionMeta.action === 'input-change') {
			setPinTitleErr(false);
			setPinTitle(input);
		}
	};
	const onPinTitleBlur = () => {
		if (!pinTitle) {
			setPinTitleErr(true);
		}
	};

	const [pinUrl, setPinUrl] = useState(isUpdateModal ? existingBookmark.properties.url : '');
	const [pinUrlErr, setPinUrlErr] = useState<PinUrlErrorType>({ isError: false });
	const onPinUrlChange = (input: string, actionMeta: InputActionMeta) => {
		if (actionMeta.action === 'input-change') {
			setPinUrlErr({ isError: false });
			setPinUrl(input);
		}
	};
	const onPinUrlBlur = () => {
		if (!pinUrl) {
			setPinUrlErr({ isError: true, type: 'empty' });
		} else if (!isValidHttpUrl(pinUrl)) {
			setPinUrlErr({ isError: true, type: 'invalid' });
		}
	};
	const renderUrlErroredMessage = () => {
		if (pinUrlErr.isError && pinUrlErr.type === 'empty') {
			return <FormattedMessage {...messages.pinModalNoUrlMessage} />;
		}

		if (pinUrlErr.isError && pinUrlErr.type === 'invalid') {
			return <FormattedMessage {...messages.pinModalInvalidUrlMessage} />;
		}
	};

	const [pinDescription, setPinDescription] = React.useState(
		isUpdateModal ? existingBookmark.properties.description : '',
	);
	const onPinDescriptionChange = (input: string, actionMeta: InputActionMeta) => {
		if (actionMeta.action === 'input-change') {
			setPinDescription(input);
		}
	};

	const [pinKeyPhrases, setPinKeyPhrases] = useState<PinSearchTermOption[]>(
		isUpdateModal
			? [
					{
						label: existingBookmark.properties.keyPhrase,
						value: existingBookmark.properties.keyPhrase,
					},
				]
			: query
				? [{ label: query, value: query }]
				: [],
	);
	const [keyPhraseErr, setKeyPhraseErr] = useState<SearchPinKeyphraseErrorState | undefined>(
		undefined,
	);
	const [collidedKeyPhrases, setCollidedKeyPhrases] = useState<string[]>([]);

	// key phrase that user has typed in input
	const [typedKeyPhrase, setTypedKeyPhrase] = useState<string>('');
	const onTypedKeyPhraseChange = (inputValue: string, event: InputActionMeta) => {
		if (event?.action === 'input-blur' || event?.action === 'menu-close') {
			return;
		}

		setKeyPhraseErr(undefined);
		setTypedKeyPhrase(inputValue);
	};

	const onPinKeyPhrasesChange = (newPins: any, _actionMeta: any) => {
		setKeyPhraseErr(undefined);
		setPinKeyPhrases(newPins);
	};

	const onPinKeyPhraseSubmit = (): boolean => {
		const normalizedKeyPhrase = typedKeyPhrase.trim().toLowerCase();
		// if nothing has been typed and user clicks tab, tab to button
		if (!normalizedKeyPhrase.length) {
			return false;
		}
		if (normalizedKeyPhrase.length > KEY_PHRASE_MAX_LENGTH) {
			setKeyPhraseErr(SearchPinKeyphraseErrorState.KEY_PHRASE_TOO_LONG);

			return true;
		}

		const specialCharsRegex = new RegExp(/[^\w\s]+|_+/);
		if (specialCharsRegex.test(normalizedKeyPhrase)) {
			setKeyPhraseErr(SearchPinKeyphraseErrorState.KEY_PHRASE_HAS_SPECIAL_CHAR);

			return true;
		}

		const updatedKeyPhrases = convertToKeyPhraseInputFormat([
			...new Set([...pinKeyPhrases.map((keyPhrase) => keyPhrase.value), normalizedKeyPhrase]),
		]);
		if (updatedKeyPhrases.length > KEY_PHRASES_LIST_MAX_LENGTH) {
			setKeyPhraseErr(SearchPinKeyphraseErrorState.KEY_PHRASE_LIST_TOO_LONG);
			setTypedKeyPhrase('');

			return true;
		}

		setPinKeyPhrases(updatedKeyPhrases);
		setTypedKeyPhrase('');

		return true;
	};

	const onPinKeyPhrasesKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (!['Tab', 'Enter'].includes(event.key)) {
			return;
		}

		const successfulSubmitOrKeyPhraseErr = onPinKeyPhraseSubmit();
		if (successfulSubmitOrKeyPhraseErr) {
			// do not want to tab to button if user still adding key phrases or needs to correct a keyphrase
			event.preventDefault();
		}
	};

	const onPinKeyPhrasesBlur = () => {
		// want to show this error only if no key phrase has been added AND user hasn't typed anything yet
		if (!pinKeyPhrases.length && !typedKeyPhrase) {
			setKeyPhraseErr(SearchPinKeyphraseErrorState.KEY_PHRASE_LIST_TOO_SHORT);

			return;
		}
		setKeyPhraseErr(undefined);
		// add what user has typed so far as a keyphrase; if invalid keyphrase show error message
		if (typedKeyPhrase) {
			onPinKeyPhraseSubmit();
		}
	};

	const onClose = () => {
		// reset
		setPinTitle('');
		setPinUrl('');
		setPinDescription('');
		setPinKeyPhrases([]);
		setPinTitleErr(false);
		setPinUrlErr({ isError: false });
		setKeyPhraseErr(undefined);

		handleClose();
	};

	const createSearchPinDisabled = !pinTitle || !isValidHttpUrl(pinUrl) || !pinKeyPhrases.length;

	const [createSearchPinLoading, setCreateSearchPinLoading] = React.useState(false);
	const { createBookmark } = useAddBookmarkMutation();
	const { updateBookmark } = useUpdateBookmarkMutation();

	const onCreateSearchPin = async () => {
		try {
			setHasGenericError(false);
			setCreateSearchPinLoading(true);
			const newPin = isUpdateModal
				? await updateBookmark({
						cloudId: cloudId,
						orgId,
						keyPhrases: pinKeyPhrases.map((keyPhrase) => keyPhrase.value),
						url: pinUrl,
						title: pinTitle,
						description: pinDescription,
						bookmarkAdminhubId: existingBookmark.properties.parentAdminhubBookmarkId ?? '',
					})
				: await createBookmark(
						{
							cloudId,
							orgId,
							keyPhrases: pinKeyPhrases.map((keyPhrase) => keyPhrase.value),
							url: pinUrl,
							title: pinTitle,
							description: pinDescription,
						},
						workspaceId,
						product,
					);

			// check for keyphrase errors from the backend
			if (newPin.errors && newPin.errors.length) {
				const collidedKeyPhrasesList = getCollidedKeyPhrases(newPin.errors);
				if (collidedKeyPhrasesList.length) {
					setKeyPhraseErr(SearchPinKeyphraseErrorState.KEY_PHRASE_COLLISION);
					setCollidedKeyPhrases(collidedKeyPhrasesList);
					setCreateSearchPinLoading(false);

					return;
				}
				throw new Error(newPin.errors[0]?.message ?? 'Generic Error');
			}

			if (isUpdateModal) {
				fireAnalyticsEvent(
					onUpdatePin(
						{
							title: pinTitle,
							url: pinUrl,
							keyPhrases: pinKeyPhrases.map((keyPhrase) => keyPhrase.value),
							description: pinDescription,
						},
						source ?? 'unknown',
					),
				);
			} else {
				fireAnalyticsEvent(
					onAddPin(
						{
							title: pinTitle,
							url: pinUrl,
							keyPhrases: pinKeyPhrases.map((keyPhrase) => keyPhrase.value),
							description: pinDescription,
						},
						source ?? 'unknown',
					),
				);
			}

			showFlag({
				isAutoDismiss: true,
				icon: <CheckCircleIcon spacing="spacious" label="" color={token('color.icon.success')} />,
				id: 'create-search-pin',
				title: isUpdateModal
					? formatMessage(messages.bookmarkUpdateSuccessFlagTitle)
					: formatMessage(messages.pinCreationSuccessFlagTitle),
				description: isUpdateModal
					? undefined
					: formatMessage(messages.pinCreationSuccessFlagMessage),
			});
			onClose();
			onSuccessCallback && onSuccessCallback();
		} catch (error) {
			setHasGenericError(true);
			fireAnalyticsEvent(onBookmarkAddFailed(isUpdateModal));
			logger.error({ error, functionName: 'onCreateSearchPin' });
		} finally {
			setCreateSearchPinLoading(false);
		}
	};

	return (
		<ModalTransition>
			<Modal onClose={onClose}>
				<ModalHeader hasCloseButton>
					<ModalTitle>
						{isUpdateModal ? (
							<FormattedMessage {...messages.bookmarkModalUpdateHeader} />
						) : (
							<FormattedMessage {...messages.pinModalAddPinHeader} />
						)}
					</ModalTitle>
				</ModalHeader>
				<Box xcss={styles.modalBody}>
					<Flex gap="space.100" direction="column">
						{hasGenericError ? (
							<Inline space="space.050" alignBlock="center">
								<ErrorIcon label="" color={token('color.text.danger')} />
								<Text color="color.text.danger">
									{isUpdateModal ? (
										<FormattedMessage {...messages.pinUpdateErrorMessage} />
									) : (
										<FormattedMessage {...messages.pinGenericErrorMessage} />
									)}
								</Text>
							</Inline>
						) : (
							<Fragment>
								<Text>
									<FormattedMessage {...messages.pinModalSubheaderLine1} />
								</Text>
								{hasAdminhubAccess && (
									<Text>
										<FormattedMessage
											{...messages.pinModalSubheaderLine2}
											values={{
												a: (chunks) => <Link href={getAdminhubLink({ orgId })}>{chunks}</Link>,
											}}
										/>
									</Text>
								)}
							</Fragment>
						)}
					</Flex>
					<Flex gap="space.200" direction="column" xcss={styles.formFieldsContainer}>
						<Flex gap="space.100" direction="column">
							<Text weight="semibold">
								<FormattedMessage {...messages.pinModalPinDetailsHeader} />
							</Text>
							<Text>
								<FormattedMessage {...messages.pinModalPinDetailsSubHeader} />
							</Text>
						</Flex>
						<Flex gap="space.050" direction="column">
							<Text size="small" weight="medium" color="color.text.subtle">
								<FormattedMessage {...messages.pinModalSearchResultTitleHeader} />
							</Text>
							<Select
								components={{
									DropdownIndicator: () => null,
									Menu: () => null,
								}}
								inputValue={pinTitle}
								onInputChange={onPinTitleChange}
								onBlur={onPinTitleBlur}
								placeholder={formatMessage(messages.pinModalSearchResultTitlePlaceholder)}
							/>
							{pinTitleErr && (
								<Inline space="space.050" alignBlock="center">
									<ErrorIcon label="" color={token('color.text.danger')} />
									<Text size="small" color="color.text.danger">
										<FormattedMessage {...messages.pinModalNoTitleMessage} />
									</Text>
								</Inline>
							)}
						</Flex>
						<Flex gap="space.050" direction="column">
							<Text size="small" weight="medium" color="color.text.subtle">
								<FormattedMessage {...messages.pinModalUrlHeader} />
							</Text>
							<Select
								components={{
									DropdownIndicator: () => null,
									Menu: () => null,
								}}
								inputValue={pinUrl}
								onInputChange={onPinUrlChange}
								onBlur={onPinUrlBlur}
								placeholder={formatMessage(messages.pinModalUrlPlaceholder)}
								testId="add-pin-modal-url-input"
							/>
							{pinUrlErr.isError && (
								<Inline space="space.050" alignBlock="center">
									<ErrorIcon label="" color={token('color.text.danger')} />
									<Text size="small" color="color.text.danger">
										{renderUrlErroredMessage()}
									</Text>
								</Inline>
							)}
						</Flex>
						<Flex gap="space.050" direction="column">
							<Text size="small" weight="medium" color="color.text.subtle">
								<FormattedMessage {...messages.pinModalDescriptionHeader} />
							</Text>
							<Select
								components={{
									DropdownIndicator: () => null,
									Menu: () => null,
								}}
								inputValue={pinDescription}
								onInputChange={onPinDescriptionChange}
								placeholder={formatMessage(messages.pinModalDescriptionPlaceholder)}
							/>
						</Flex>
						<Flex gap="space.050" direction="column">
							<Inline space="space.050">
								<Text size="small" weight="medium" color="color.text.subtle">
									<FormattedMessage {...messages.pinModalSearchForTermsHeader} />
								</Text>
								<Tooltip
									content={formatMessage(messages.pinModalSearchForTermsTooltip)}
									position="bottom-start"
								>
									<QuestionCircleIcon color="currentColor" label="" />
								</Tooltip>
							</Inline>
							<CreatableSelect
								components={{
									DropdownIndicator: () => null,
									Menu: () => null,
								}}
								isMulti
								inputValue={typedKeyPhrase}
								onInputChange={onTypedKeyPhraseChange}
								defaultValue={pinKeyPhrases}
								value={pinKeyPhrases}
								onKeyDown={onPinKeyPhrasesKeyDown}
								onChange={onPinKeyPhrasesChange}
								onBlur={onPinKeyPhrasesBlur}
								placeholder={formatMessage(messages.pinModalSearchForTermsPlaceholder)}
								testId="add-pin-modal-key-phrase-input"
							/>
							{keyPhraseErr && (
								<Inline space="space.050" alignBlock="center">
									<ErrorIcon label="" color={token('color.text.danger')} />
									<Text size="small" color="color.text.danger">
										{getKeyPhraseErrMessage(
											keyPhraseErr,
											formatList(collidedKeyPhrases, { style: 'long', type: 'conjunction' }),
											collidedKeyPhrases.length,
										)}
									</Text>
								</Inline>
							)}
						</Flex>
					</Flex>
				</Box>
				<ModalFooter>
					<Button appearance="subtle" onClick={onClose} testId="add-search-pin-cancel-button">
						<FormattedMessage {...messages.pinModalCancelButton} />
					</Button>
					<Button
						appearance="primary"
						isDisabled={createSearchPinDisabled}
						onClick={onCreateSearchPin}
						isLoading={createSearchPinLoading}
						testId="bookmark-submit-button"
					>
						{isUpdateModal ? (
							<FormattedMessage {...messages.bookmarkModalUpdateButton} />
						) : (
							<FormattedMessage {...messages.pinModalAddButton} />
						)}
					</Button>
				</ModalFooter>
			</Modal>
		</ModalTransition>
	);
};
